import { runtimeBus } from './runtimeBus';
import { isRankingSubmission, type RankingBoard, type RankingCategory, type RankingSubmission } from '../game/ranking/types';
import type { SessionEvent, SessionState } from '../game/simulation/types';
import { EMPTY_PROFILE, type PlayerProfile, type ProfileEdit } from '../game/profile/types';

const DRAFT_KEY = 'ishi-no-kyomei.ranking-pending.v1';

export class RankingController {
  public name: string | null = null;
  public profile: PlayerProfile = EMPTY_PROFILE;
  public state: SessionState | null = null;
  public eligible = true;
  public error = '';
  public saving = false;
  public lastSavedRunId: string | null = null;
  public personalBest = { score: false, time: false };
  private auto = false;
  private initialized = false;
  private profileRequest = 0;
  private accountBusy = false;
  private readonly pending = new Map<string, RankingSubmission>();
  private readonly listeners = new Set<() => void>();

  public constructor() {
    try {
      const draft: unknown = JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? '[]');
      if (Array.isArray(draft)) for (const value of draft.slice(-20)) if (isRankingSubmission(value)) this.pending.set(value.runId, value);
    } catch { /* A missing temporary draft never prevents play. */ }
    runtimeBus.onAuto(enabled => {
      this.auto = enabled;
      if (enabled && this.state?.status !== 'game-over') this.eligible = false;
      this.changed();
    });
    runtimeBus.onState((state, events) => this.observe(state, events));
    window.addEventListener('online', () => { void this.retry(); });
  }

  public get pendingCount(): number { return this.pending.size; }
  public get autoEnabled(): boolean { return this.auto; }
  public onChange(listener: () => void): void { this.listeners.add(listener); }

  public async initialize(): Promise<void> {
    try {
      await this.refreshProfile();
      this.error = '';
      await this.flush();
    } catch (error) { this.error = message(error); }
    this.changed();
  }

  public async register(name: string): Promise<void> {
    await this.writeProfile({ name });
  }

  public async saveProfile(edit: ProfileEdit): Promise<void> { await this.writeProfile(edit); }

  public async refreshProfile(): Promise<PlayerProfile> {
    if (this.accountBusy) return this.profile;
    const revision = ++this.profileRequest;
    try {
      const profile = await this.request<PlayerProfile>('/profile');
      if (revision === this.profileRequest) this.applyProfile(profile);
    } catch (error) { if (revision === this.profileRequest) throw error; }
    return this.profile;
  }

  private async writeProfile(edit: Partial<ProfileEdit> & { name: string }): Promise<void> {
    if (this.accountBusy) throw new Error('プロフィールの保存が終わるまでお待ちください。');
    this.accountBusy = true;
    ++this.profileRequest;
    try { this.applyProfile(await this.request<PlayerProfile>('/profile', edit)); }
    finally { this.accountBusy = false; }
    this.error = '';
    await this.flush();
    this.changed();
  }

  public async issueRecoveryCode(): Promise<string> {
    if (this.accountBusy) throw new Error('プロフィールの処理が終わるまでお待ちください。');
    this.accountBusy = true;
    ++this.profileRequest;
    try {
      const result = await this.request<{ profile: PlayerProfile; recoveryCode: string }>('/recovery/issue', {});
      this.applyProfile(result.profile);
      return result.recoveryCode;
    } finally { this.accountBusy = false; }
  }

  public async restoreProfile(code: string): Promise<string> {
    if (this.accountBusy || this.saving) throw new Error('保存・送信が終わるまでお待ちください。');
    if (this.pending.size) throw new Error('未送信の記録があります。今のプロフィールで送信してから復元してください。');
    this.accountBusy = true;
    ++this.profileRequest;
    try {
      const result = await this.request<{ profile: PlayerProfile; recoveryCode: string }>('/recovery/restore', { code });
      if (this.state && this.state.status !== 'ready' && this.state.status !== 'game-over') this.eligible = false;
      this.lastSavedRunId = null;
      this.personalBest = { score: false, time: false };
      this.error = '';
      this.applyProfile(result.profile);
      return result.recoveryCode;
    } finally { this.accountBusy = false; }
  }

  private applyProfile(profile: PlayerProfile): void {
    this.profile = { ...EMPTY_PROFILE, ...profile, stats: profile.stats ?? EMPTY_PROFILE.stats };
    this.name = profile.name;
    this.initialized = true;
    this.changed();
  }

  public async board(category: RankingCategory): Promise<RankingBoard> {
    return this.request<RankingBoard>(`/board?category=${category}`);
  }

  public async retry(): Promise<void> {
    if (!this.initialized) return this.initialize();
    await this.flush();
  }

  private observe(state: SessionState, events: readonly SessionEvent[]): void {
    if (this.state?.runId !== state.runId) {
      if (this.state && this.state.status !== 'ready' && this.state.status !== 'game-over') this.queue(this.state);
      this.eligible = !this.auto && state.mode === 'classic';
    }
    if (state.entities.some(entity => entity.origin === 'scenario')) this.eligible = false;
    this.state = state;
    if (events.some(event => event.type === 'run-cleared' || event.type === 'game-over')) this.queue(state);
    this.changed();
  }

  private queue(state: SessionState): void {
    if (state.mode !== 'classic' || !this.eligible || (state.score === 0 && state.dropSequence === 0)) return;
    const previous = this.pending.get(state.runId);
    const value: RankingSubmission = {
      runId: state.runId, score: state.score, stage: state.highestStage, chain: state.maxChain,
      elapsedMs: Math.max(Math.round(state.gameplayTimeMs), state.clearTimeMs ?? 0),
      clearTimeMs: previous?.clearTimeMs ?? state.clearTimeMs,
    };
    this.pending.set(state.runId, value);
    this.saveDraft();
    void this.flush();
  }

  private async flush(): Promise<void> {
    if (this.saving || this.accountBusy || !this.name || !this.pending.size) return;
    this.saving = true;
    this.error = '';
    this.changed();
    try {
      for (const [id, value] of this.pending) {
        const response = await this.request<{ personalBest: { score: boolean; time: boolean } }>('/runs', value);
        // A clear can be followed by a higher score while this request is in flight.
        if (this.pending.get(id) === value) this.pending.delete(id);
        this.lastSavedRunId = id;
        this.personalBest = response.personalBest;
        this.saveDraft();
      }
    } catch (error) { this.error = message(error); }
    finally { this.saving = false; this.changed(); }
    if (this.pending.size && !this.error) await this.flush();
  }

  private saveDraft(): void {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify([...this.pending.values()])); }
    catch { this.error = '未送信の記録を一時保存できません。ページを閉じる前に再送してください。'; }
  }

  private changed(): void { this.listeners.forEach(listener => listener()); }

  private async request<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(`/api/ranking${path}`, {
      method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store',
      ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
      signal: AbortSignal.timeout(10_000),
    });
    if (!response.headers.get('content-type')?.includes('application/json')) throw new Error('ランキングに接続できません。時間をおいて再試行してください。');
    const data = await response.json() as T & { error?: string };
    if (!response.ok) throw new Error(data.error ?? 'ランキングの通信に失敗しました。');
    return data;
  }
}

function message(error: unknown): string {
  return error instanceof Error && error.name !== 'TimeoutError' && error.name !== 'TypeError'
    ? error.message : 'ランキングに接続できません。未送信の記録は、接続後に再送できます。';
}
